'use client';

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

/**
 * TagInput Component Props
 */
export interface TagInputProps {
  /** Label text displayed above the input */
  label: string;
  /** Array of selected tag values */
  value: string[];
  /** Available options for selection */
  options: { label: string; value: string }[];
  /** Callback when tags change */
  onChange: (value: string[]) => void;
  /** Placeholder text when no tags selected */
  placeholder?: string;
  /** Optional help text */
  helpText?: string;
  /** Maximum number of tags allowed */
  maxTags?: number;
  /** Disabled state */
  disabled?: boolean;
  /** Optional CSS class name */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  line-height: 1;
`;

const InputContainer = styled.div<{ $focused: boolean; $disabled?: boolean }>`
  position: relative;
  background: #ffffff;
  border: 1px solid ${({ $focused }) => ($focused ? '#FB6F2A' : '#D1D5DB')};
  border-radius: 8px;
  padding: 8px 40px 8px 12px;
  min-height: 44px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'text')};
  transition: all 0.2s ease;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};

  ${({ $focused, $disabled }) =>
    $focused &&
    !$disabled &&
    `
    box-shadow: 0 0 0 3px rgba(251, 111, 42, 0.2);
  `}

  &:hover {
    border-color: ${({ $focused, $disabled }) =>
      $disabled ? '#D1D5DB' : $focused ? '#FB6F2A' : '#9CA3AF'};
  }
`;

const Tag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(270deg, #D33324 0%, #FB6F2A 100%);
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 10px;
  border-radius: 6px;
  line-height: 1;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    max-width: 150px;
  }
`;

const TagText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  padding: 0;
  background: transparent;
  border: none;
  color: #ffffff;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.2s ease;
  flex-shrink: 0;

  &:hover {
    opacity: 1;
  }

  &:focus {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 1px;
    border-radius: 2px;
  }

  svg {
    width: 10px;
    height: 10px;
  }
`;

const Placeholder = styled.span`
  color: #9CA3AF;
  font-size: 14px;
  user-select: none;
`;

const Controls = styled.div`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 4px;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: none;
  color: #6B7280;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: #F3F4F6;
    color: #111827;
  }

  &:focus {
    outline: 2px solid rgba(251, 111, 42, 0.4);
    outline-offset: 2px;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const Dropdown = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transform: translateY(${({ $visible }) => ($visible ? '0' : '-8px')});
  transition: all 0.2s ease;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f5f5f5;
  }

  &::-webkit-scrollbar-thumb {
    background: #D1D5DB;
    border-radius: 3px;
  }
`;

const DropdownOption = styled.button<{ $selected: boolean }>`
  width: 100%;
  padding: 10px 12px;
  background: ${({ $selected }) =>
    $selected ? 'rgba(251, 111, 42, 0.1)' : 'transparent'};
  border: none;
  color: #111827;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ $selected }) =>
      $selected ? 'rgba(251, 111, 42, 0.2)' : '#f5f5f5'};
  }

  &:focus {
    outline: none;
    background: rgba(251, 111, 42, 0.2);
  }
`;

// Icons
const XIcon = () => (
  <svg viewBox="0 0 10 10" fill="none">
    <path
      d="M1 1L9 9M9 1L1 9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const ChevronIcon = () => (
  <svg viewBox="0 0 14 14" fill="none">
    <path
      d="M3.5 5.25L7 8.75L10.5 5.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ============================================================================
// Component
// ============================================================================

/**
 * TagInput Component
 * 
 * Multi-select input with removable tag pills and dropdown selector.
 * Perfect for search filters, category selection, etc.
 * 
 * @component
 * @example
 * ```tsx
 * <TagInput
 *   label="Search In:"
 *   value={['basic', 'decoded']}
 *   options={[
 *     { label: 'Basic', value: 'basic' },
 *     { label: 'Decoded', value: 'decoded' },
 *     { label: 'Message', value: 'message' },
 *   ]}
 *   onChange={setSearchFields}
 *   helpText="Select fields to search within"
 * />
 * ```
 */
export const TagInput: React.FC<TagInputProps> = ({
  label,
  value,
  options,
  onChange,
  placeholder = 'Select options...',
  helpText,
  maxTags,
  disabled = false,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRemoveTag = (tagValue: string) => {
    if (!disabled) {
      onChange(value.filter((v) => v !== tagValue));
    }
  };

  const handleToggleTag = (tagValue: string) => {
    if (disabled) return;

    if (value.includes(tagValue)) {
      onChange(value.filter((v) => v !== tagValue));
    } else {
      if (!maxTags || value?.length < maxTags) {
        onChange([...value, tagValue]);
      }
    }
  };

  const handleClearAll = () => {
    if (!disabled) {
      onChange([]);
    }
  };

  const handleContainerClick = () => {
    if (!disabled) {
      setIsFocused(true);
    }
  };

  const getTagLabel = (tagValue: string) => {
    const option = options.find((opt) => opt.value === tagValue);
    return option ? option.label : tagValue;
  };

  return (
    <Container className={className} ref={containerRef}>
      <LabelRow>
        <Label>{label}</Label>
      </LabelRow>
      
      <InputContainer
        $focused={isFocused}
        $disabled={disabled}
        onClick={handleContainerClick}
      >
        {value?.length === 0 ? (
          <Placeholder>{placeholder}</Placeholder>
        ) : (
          value.map((tagValue) => (
            <Tag key={tagValue}>
              <TagText>{getTagLabel(tagValue)}</TagText>
              <RemoveButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveTag(tagValue);
                }}
                aria-label={`Remove ${getTagLabel(tagValue)}`}
                type="button"
              >
                <XIcon />
              </RemoveButton>
            </Tag>
          ))
        )}

        <Controls onClick={(e) => e.stopPropagation()}>
          {value?.length > 0 && (
            <IconButton
              onClick={handleClearAll}
              aria-label="Clear all"
              title="Clear all"
              type="button"
            >
              <XIcon />
            </IconButton>
          )}
          <IconButton
            onClick={() => !disabled && setIsOpen(!isOpen)}
            aria-label="Toggle dropdown"
            type="button"
          >
            <ChevronIcon />
          </IconButton>
        </Controls>

        <Dropdown $visible={isOpen && !disabled}>
          {options.map((option) => (
            <DropdownOption
              key={option.value}
              $selected={value.includes(option.value)}
              onClick={() => handleToggleTag(option.value)}
              type="button"
            >
              {option.label}
              {value.includes(option.value) && ' ✓'}
            </DropdownOption>
          ))}
        </Dropdown>
      </InputContainer>
    </Container>
  );
};

export default TagInput;

